import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileSpreadsheet } from "lucide-react";

export default function OwnWithdrawalsReport() {
  const { year } = useParams();
  const nav = useNavigate();
  const { t, fmtMoney, fmtDate } = useI18n();
  const [rows, setRows] = useState(null);

  useEffect(() => { api.get(`/own-withdrawals?year=${year}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [year]);
  if (rows === null) return <div className="text-muted-foreground p-8">{t("common.loading")}</div>;

  const byPerson = {};
  for (const r of rows) {
    const p = r.created_by_email || "—";
    (byPerson[p] = byPerson[p] || []).push(r);
  }
  const grand = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
  const grandPaid = rows.filter((r) => r.ek_paid).reduce((s, r) => s + (r.total_cost || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="ownw-report-back"><ArrowLeft className="h-4 w-4" /> {t("common.back")}</button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadFile(`/own-withdrawals/export.xlsx?year=${year}`, `eigenentnahmen_${year}.xlsx`)} data-testid="ownw-report-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button onClick={() => window.print()} data-testid="ownw-report-print"><Printer className="h-4 w-4 mr-1.5" /> {t("common.printPdf")}</Button>
        </div>
      </div>

      <div className="a4-page mx-auto max-w-3xl rounded-md border border-border bg-white text-black p-10 shadow-sm" data-testid="ownw-report-doc" style={{ fontFamily: "'Lucida Sans', 'Lucida Grande', sans-serif" }}>
        <div className="flex justify-between items-start">
          <div>
            <div className="text-2xl font-bold">{t("ow.reportTitle", { year })}</div>
            <div className="text-xs text-neutral-600">Gluty-Free Club · Consberry GmbH · {t("ow.valuationNote")}</div>
          </div>
          <div className="text-right text-xs text-neutral-600">
            <div>{t("common.created")}: {fmtDate(new Date().toISOString())}</div>
            <div>{t("ow.positions", { count: rows.length })}</div>
          </div>
        </div>

        <p className="mt-4 text-xs text-neutral-600">{t("ow.reportIntro")}</p>

        {Object.entries(byPerson).map(([person, list]) => {
          const sub = list.reduce((s, r) => s + (r.total_cost || 0), 0);
          return (
            <div key={person} className="mt-6">
              <h3 className="mb-1 text-base font-semibold">{person}</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-neutral-300 text-left">
                  <th className="py-1">{t("common.date")}</th><th>SKU</th><th>{t("common.item")}</th>
                  <th className="text-right">{t("common.quantity")}</th><th className="text-right">{t("ow.costPerUnit")}</th><th className="text-right">{t("ow.costSum")}</th><th className="text-right">{t("common.paid")}</th>
                </tr></thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="border-b border-neutral-100">
                      <td className="py-1 whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                      <td className="font-mono">{r.sku}</td><td>{r.product_name}</td>
                      <td className="text-right font-mono">{r.quantity}</td>
                      <td className="text-right font-mono">{fmtMoney(r.unit_cost)}</td>
                      <td className="text-right font-mono">{fmtMoney(r.total_cost)}</td>
                      <td className="text-right">{r.ek_paid ? t("common.yes") : t("common.no")}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold"><td colSpan={5} className="py-1 text-right">{t("ow.subtotal", { person })}</td><td className="text-right font-mono">{fmtMoney(sub)}</td><td /></tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <div className="mt-8 border-t-2 border-neutral-400 pt-3 flex justify-end gap-8 text-sm">
          <div className="text-right">
            <div className="text-neutral-600">{t("ow.grandTotal", { year })}</div>
            <div className="text-xl font-bold font-mono">{fmtMoney(grand)}</div>
          </div>
          <div className="text-right">
            <div className="text-neutral-600">{t("ow.thereofPaid")}</div>
            <div className="text-xl font-bold font-mono">{fmtMoney(grandPaid)}</div>
          </div>
          <div className="text-right">
            <div className="text-neutral-600">{t("common.open")}</div>
            <div className="text-xl font-bold font-mono">{fmtMoney(grand - grandPaid)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
